import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { KeyRounded } from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import { useNetwork } from '../../../utils/network';
import { dismissRecoveryCodesPrompt, recoveryCodesPromptDismissed } from '../../../utils/recoveryCodesPrompt';
import { RecoveryCodesGenerateDialog, type RecoveryCodesStatus } from './RecoveryCodesGenerateDialog';
import { PT, dialogPaperSx, ghostBtnSx, kickerSx, primaryBtnSx } from '../../../components/portalUi';

/* On arrival, for a member with two-factor authentication on and NO recovery codes:
 * generate them now. The codes are the way back in when the phone is lost; without
 * them the only way is a reset request and its 72-hour wait. Members who turned 2FA
 * on before recovery codes existed have none, and nothing else tells them.
 *
 * Asked once per sign-in ("Not now" lasts until the next sign-in —
 * utils/recoveryCodesPrompt.ts) and never again once a set exists: the status is
 * asked for on every portal arrival, so generating from Settings ends it too. The
 * generation itself is the same dialog as Settings → Security. */
export const RecoveryCodesPrompt = () => {
  const { user } = useUser();
  const { get } = useNetwork();
  const [status, setStatus] = useState<RecoveryCodesStatus | null>(null);
  const [dismissed, setDismissed] = useState(recoveryCodesPromptDismissed);
  const [generating, setGenerating] = useState(false);

  const twoFactorOn = Boolean(user?.hasTwoFactorAuthEnabled);

  useEffect(() => {
    if (!twoFactorOn) return undefined;
    let alive = true;
    get('/user/otp/recovery-codes/status', true)
      .then((result) => {
        if (!alive) return;
        setStatus({
          remaining: typeof result?.remaining === 'number' ? result.remaining : 0,
          generatedAt: result?.generatedAt ?? null
        });
      })
      .catch(() => {
        /* nothing to say: the prompt is extra, not the only way to the codes */
      });
    return () => {
      alive = false;
    };
    // once per portal mount, deliberately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoFactorOn]);

  const needed = twoFactorOn && status !== null && status.generatedAt === null;
  /* Generating flips the status, which would unmount this — and the dialog that is
     showing the codes with it, once and never again. The dialog stays until its Done. */
  if (!needed && !generating) return null;

  const notNow = () => {
    dismissRecoveryCodesPrompt();
    setDismissed(true);
  };

  return (
    <>
      <Dialog
        open={needed && !dismissed && !generating}
        onClose={notNow}
        fullWidth
        maxWidth="xs"
        scroll="paper"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography sx={kickerSx}>Two-factor authentication</Typography>
          <Typography sx={{ mt: 0.75, fontWeight: 800, fontSize: 18, color: PT.text, lineHeight: 1.25 }}>
            Save your recovery codes
          </Typography>
        </Box>
        <DialogContent sx={{ px: 3, pt: 0.5, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <KeyRounded sx={{ color: PT.gold, flexShrink: 0, mt: '2px' }} />
            <Typography sx={{ fontSize: 14, color: PT.textMuted, lineHeight: 1.55 }}>
              Your account has two-factor authentication on but no recovery codes. If you lose your
              phone, a recovery code signs you in straight away; without one, getting back in means a
              reset request and a 72-hour wait. Generate your codes now and download the file somewhere
              safe. It takes a minute.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button onClick={notNow} sx={{ ...ghostBtnSx, px: 2.5, py: 1 }}>
            Not now
          </Button>
          <Button onClick={() => setGenerating(true)} sx={{ ...primaryBtnSx, px: 3, py: 1 }}>
            Generate my codes
          </Button>
        </DialogActions>
      </Dialog>

      <RecoveryCodesGenerateDialog
        open={generating}
        intro="Confirm it’s you to generate your codes. They are shown once: download the file or copy them somewhere safe before you close it."
        onClose={() => setGenerating(false)}
        onGenerated={(s) => setStatus(s)}
      />
    </>
  );
};
